import React, { useState, useEffect } from "react";
import Layout from "../Shared/Layout";
import { getOrderByIssuerAccountId } from "../service/api/Order";
import useSelectedDomain from "../service/Hooks/useSelectedDomain";
import { useUserData } from "../service/api/useUserData";

const OrderPage = () => {
  // Ces identifiants pourraient provenir du contexte ou des props
  const fromAccountId = 1;

  const [orders, setOrders] = useState([]); // Tous les ordres chargés
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

//   gestion du domain
  const selectedDomain = useSelectedDomain(); 

  // Définir la taille par page pour la pagination côté client
  const pageSize = 10;

  // gerer l'id de l'utilisateur connecte
	const { getUserInfo } = useUserData();
	const [creatorId, setCreatorId] = useState(null);

	useEffect(() => {
		async function fetchUserData() {
			const userInfo = await getUserInfo();
			if (userInfo) {
				setCreatorId(userInfo.accountId);
			}
		}
		fetchUserData();
	}, []);
	  // fin de la gestion de l'id de l'utilisateur connecte

  // Fonction qui charge tous les ordres
  // Ici, on suppose que l'API est paginée. Pour charger *tous* les ordres,
  // on peut soit augmenter le pageSize, soit faire plusieurs appels.
  // Pour cet exemple, nous allons supposer que le nombre total d'ordres n'est pas très élevé
  // et utiliser un appel avec un grand pageSize.
  const fetchAllOrders = async () => {
	if(!selectedDomain || !selectedDomain.id) return; 
    setLoading(true);
    try {
      // On pourrait par exemple utiliser un grand nombre pour le pageSize
      const response = await getOrderByIssuerAccountId(creatorId, selectedDomain.id, 1, 1000);
      // Filtrer pour ne garder que les status "Completed" ou "Canceled"
      const filtered = response.filter(
        (order) =>
          order.status === "Completed" || order.status === "Canceled"
      );
      setOrders(filtered);
    } catch (error) {
      console.error("Erreur lors du chargement des ordres :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders();
  }, [creatorId,selectedDomain]);

  // Filtrage par recherche (par marché)
  const filteredOrders = orders.filter((order) => {
    // On vérifie que le marché (par exemple order.market ou order.marche) correspond à la recherche
    // en ignorant la casse.
    if (searchQuery.trim() === "") return true;
    return order.market?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Calculer le nombre total de pages
  const totalPages = Math.ceil(filteredOrders.length / pageSize);

  // Découper les ordres à afficher selon la page courante
  const displayedOrders = filteredOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Gérer le changement de page
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Gestion de la recherche : déclenchée à l'appui sur la touche "Enter"
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Réinitialiser la pagination sur la première page lors d'une nouvelle recherche
      setCurrentPage(1);
    }
  };

  function formatDate(dateString) {
	const date = new Date(dateString);
	const options = { day: 'numeric', month: 'short', year: 'numeric' };
	return date.toLocaleDateString('fr-FR', options);
  }

  return (
    <Layout>
      <div className="content-body default-height">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-12">
              <div className="page-titles">
                <nav style={{ "--bs-breadcrumb-divider": "'>'" }} aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item text-primary">Ordres</li>
                  </ol>
                </nav>
                <div className="d-flex flex-wrap my-2 my-sm-0">
                  <div className="input-group search-area-solid w-auto me-3">
                    <div className="input-group-text solid pe-2">
                      <a href="javascript:void(0)">
                        {/* Icône de recherche */}
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M16.6751 19.4916C16.2194 19.036 16.2194 18.2973 16.6751 17.8417C17.1307 17.3861 17.8694 17.3861 18.325 17.8417L22.9916 22.5084C23.4473 22.964 23.4473 23.7027 22.9916 24.1583C22.536 24.6139 21.7973 24.6139 21.3417 24.1583L16.6751 19.4916Z" fill="var(--primary)"/>
                          <path d="M12.8333 18.6667C16.055 18.6667 18.6667 16.055 18.6667 12.8334C18.6667 9.61169 16.055 7.00002 12.8333 7.00002C9.61166 7.00002 6.99999 9.61169 6.99999 12.8334C6.99999 16.055 9.61166 18.6667 12.8333 18.6667ZM12.8333 21C8.323 21 4.66666 17.3437 4.66666 12.8334C4.66666 8.32303 8.323 4.66669 12.8333 4.66669C17.3436 4.66669 21 8.32303 21 12.8334C21 17.3437 17.3436 21 12.8333 21Z" fill="var(--primary)"/>
                        </svg>
                      </a>
                    </div>
                    <input
                      type="text"
                      className="form-control solid border-start-0 ps-0"
                      placeholder="Recherche par type de marche..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body py-3">
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
							                <th>ID</th>
                              <th className="align-middle">Titres</th>
                              <th className="align-middle pe-7">Marche cible</th>
                              <th className="align-middle text-end">Type d'action</th>
                              <th className="align-middle text-end">Quantitée</th>
                              <th className="align-middle text-end">Prix</th>
                              <th className="align-middle text-end">Status</th>
                              <th className="align-middle text-end">Date création</th>
                              <th className="align-middle text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody id="orders">
                            {loading ? (
                              <tr>
                                <td colSpan="9" className="text-center">Chargement...</td>
                              </tr>
                            ) : displayedOrders.length > 0 ? (
                              displayedOrders.map((order, index) => (
                                <tr key={index} className="btn-reveal-trigger">
									<td className="text-muted">{order.id}</td>
                                  <td className="py-2">
                                    <a href="#">
                                      <strong className="text-primary">{order.securityName || "Nom titre"}</strong>
                                    </a>{" "}
                                    by <strong className="text-primary">{order.accountName || "Porteur"}</strong>
                                  </td>
                                  <td className="py-2 text-end">{order.targetMarketName}</td>
                                  <td className="py-2 text-end">{order.domain || "Uba"}</td>
                                  <td className="py-2 text-end">{order.side || "Buy"}</td>
                                  <td className="py-2 text-end">{order.quantity || 0}</td>
                                  <td className="py-2 text-end">{order.price ? `${order.price} Fcfa` : "0 Fcfa"}</td>
                                  <td className="py-2 text-end">
                                    {order.status === "Completed" ? (
                                      <span className="badge badge-success badge-sm light">
                                        {order.status}
                                        <span className="ms-1 fa fa-check"></span>
                                      </span>
                                    ) : order.status === "Canceled" ? (
                                      <span className="badge badge-danger badge-sm light">
                                        {order.status}
                                        <span className="ms-1 fa fa-times"></span>
                                      </span>
                                    ) : (
                                      <span className="badge badge-primary badge-sm light">
                                        {order.status}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 text-center">{formatDate(order.creationDate) || "Date inconnue"}</td>
                                  <td className="py-2 text-end">
                                    <div className="dropdown text-sans-serif">
                                      <button
                                        className="btn btn-primary tp-btn-light sharp"
                                        type="button"
                                        id={`order-dropdown-${index}`}
                                        data-bs-toggle="dropdown"
                                        data-boundary="viewport"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                      >
                                        <span>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            xmlnsXlink="http://www.w3.org/1999/xlink"
                                            width="18px"
                                            height="18px"
                                            viewBox="0 0 24 24"
                                            version="1.1"
                                          >
                                            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                              <rect x="0" y="0" width="24" height="24"></rect>
                                              <circle fill="#000000" cx="5" cy="12" r="2"></circle>
                                              <circle fill="#000000" cx="12" cy="12" r="2"></circle>
                                              <circle fill="#000000" cx="19" cy="12" r="2"></circle>
                                            </g>
                                          </svg>
                                        </span>
                                      </button>
                                      <div className="dropdown-menu dropdown-menu-end border py-0" aria-labelledby={`order-dropdown-${index}`}>
                                        <div className="py-2">
                                          <a className="dropdown-item text-green" href="javascript:void(0);">Voir plus</a>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center text-muted">Aucun ordre trouvé</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="mt-5">
                        <nav aria-label="Page navigation">
                          <ul className="pagination justify-content-center mb-0">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                              <a className="page-link" href="#prev" aria-label="Previous" onClick={() => handlePageChange(currentPage - 1)}>
                                <i className="fa-solid fa-angle-left"></i>
                              </a>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
                                <a className="page-link" href={`#${page}`} onClick={() => handlePageChange(page)}>
                                  {page}
                                </a>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                              <a className="page-link" href="#next" aria-label="Next" onClick={() => handlePageChange(currentPage + 1)}>
                                <i className="fa-solid fa-angle-right"></i>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderPage;
